import { Button, Divider, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import Sticky from 'react-sticky-el';
import BtnSave from './BtnSave';
import Editor from '../../../../components/Shared/forms/EditorPrivacy';
import TextBox from '../../Shared/TextBox';
import { EXAMLE, INIT } from './init';
import { useEffect, useState } from 'react';
import { checkValidForm } from './Step1';
import { POLICY_NAME } from '../../POLICY';

const Step5 = (props) => {
    const { previewData, setStep5 } = props;
    const { t } = useTranslation(['translation', 'cookies-policy']);
    const [haveError, setHaveError] = useState(false);

    useEffect(() => {
        if (previewData) {
            let check = true;
            const allowKey = [
                'cookieChangeTitleTh',
                'cookieChangeTitleEn',
                'cookieDataControllerTitleTh',
                'cookieDataControllerTitleEn',
                'cookieDataControllerContentTh',
                'cookieDataProtectionTitleTh',
                'cookieDataProtectionTitleEn',
                'cookieDataProtectionContentTh',
            ];
            for (const [key, value] of Object.entries(
                previewData?.cookiePolicyChange
            )) {
                if (allowKey.includes(key) && !value) {
                    check = false;
                }
            }
            setStep5(check);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewData]);

    const onNextStep = () => {
        if (props.passStep) {
            props.nextStep();
            props?.form
                .validateFields()
                .then(() => {
                    props.setStep5(true);
                    setHaveError(false);
                })
                .catch(() => {
                    props.setStep5(false);
                    setHaveError(true);
                });
        } else {
            setHaveError(true);
        }
    };

    useEffect(() => {
        if (props.passStep) {
            setHaveError(false);
        }
    }, [props.passStep]);

    const onPreviousStep = () => {
        props.previousStep();
    };

    useEffect(() => {
        if (props?.previewData?.cookiePolicyChange) {
            onValuesChange(null, props.previewData?.cookiePolicyChange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.previewData]);

    const onValuesChange = (change, allValues) => {
        const fields = [
            'cookieChangeTitleTh',
            'cookieChangeTitleEn',
            'cookieDataControllerTitleTh',
            'cookieDataControllerTitleEn',
            'cookieDataControllerContentTh',
            'cookieDataProtectionTitleTh',
            'cookieDataProtectionTitleEn',
            'cookieDataProtectionContentTh',
        ];
        const check = checkValidForm(fields, allValues);
        props.setStep5(check);
    };

    return (
        <div className="px-4 block_">
            <div className="text-purple-PHX font-semibold text-2xl">
                {t('cookies-policy.Policy change and contact channels', {
                    ns: POLICY_NAME.COOKIES_POLICY,
                })}
            </div>
            <div className="text-gray-500 text-tiny">{t('policy.desEtc')}</div>
            <Divider />
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                onValuesChange={onValuesChange}
                name="cookiePolicyChange"
                layout="vertical"
                form={props?.form}
                initialValues={INIT.cookiePolicyChange}
            >
                <div>
                    <div className="text-purple-PHX font-semibold text-xl">
                        {t('Changes to the Cookies Policy', {
                            ns: POLICY_NAME.COOKIES_POLICY,
                        })}
                    </div>
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'TH',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieChangeTitleTh"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <div className="text-sm font-semibold">
                        {t('Example Data')}
                    </div>
                    <TextBox
                        code={EXAMLE.cookiePolicyChange.cookieChangeContentTh}
                    />
                    <Form.Item
                        className="mt-4"
                        name="cookieChangeContentTh"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'TH' })}
                            </span>
                        }
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'TH' })}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'EN',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieChangeTitleEn"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <div className="text-sm font-semibold">
                        {t('Example Data')}
                    </div>
                    <TextBox
                        code={EXAMLE.cookiePolicyChange.cookieChangeContentEn}
                    />
                    <Form.Item
                        className="mt-4"
                        name="cookieChangeContentEn"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'EN' })}
                            </span>
                        }
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'EN' })}
                        />
                    </Form.Item>
                </div>
                <Divider />
                <div>
                    <div className="text-purple-PHX font-semibold text-xl">
                        ผู้ควบคุมข้อมูลส่วนบุคคล (Data Controller)
                    </div>
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'TH',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieDataControllerTitleTh"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>

                    <Form.Item
                        className="mt-4"
                        name="cookieDataControllerContentTh"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'TH' })}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'TH' })}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'EN',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieDataControllerTitleEn"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <Form.Item
                        className="mt-4"
                        name="cookieDataControllerContentEn"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'EN' })}
                            </span>
                        }
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'EN' })}
                        />
                    </Form.Item>
                </div>

                <Divider />
                <div>
                    <div className="text-purple-PHX font-semibold text-xl">
                        เจ้าหน้าที่คุ้มครองข้อมูลส่วนบุคคล (Data Protection
                        Officer: DPO)
                    </div>
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'TH',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieDataProtectionTitleTh"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>

                    <Form.Item
                        className="mt-4"
                        name="cookieDataProtectionContentTh"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'TH' })}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'TH' })}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'EN',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieDataProtectionTitleEn"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <Form.Item
                        className="mt-4"
                        name="cookieDataProtectionContentEn"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'EN' })}
                            </span>
                        }
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'EN' })}
                        />
                    </Form.Item>
                    <Form.Item hidden name="cookieChangeIsDisplay"></Form.Item>
                    <Form.Item
                        hidden
                        name="cookieDataControllerIsDisplay"
                    ></Form.Item>
                    <Form.Item
                        hidden
                        name="cookieDataProtectionIsDisplay"
                    ></Form.Item>
                </div>

                <Sticky
                    mode="bottom"
                    boundaryElement=".block_"
                    stickyClassName="bg-white p-4 shadow h-16 rounded-lg z-50"
                >
                    <div className="flex items-center">
                        <Button
                            type="default"
                            size="large"
                            onClick={onPreviousStep}
                        >
                            <ArrowLeftOutlined /> {t('policy.PreviousStep')}
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            className="ml-4"
                            onClick={onNextStep}
                        >
                            {t('Preview before publishing')}{' '}
                            <ArrowRightOutlined />
                        </Button>
                        <BtnSave saveDraft={props.saveDraft} />
                    </div>
                    {haveError && (
                        <div className="text-red-500 space-x-2 mt-2">
                            <InfoCircleOutlined />
                            <span>
                                {t(
                                    'Unable to preview before release Please complete the required information on each tab'
                                )}
                            </span>
                        </div>
                    )}
                </Sticky>
            </Form>
        </div>
    );
};

export default Step5;
